/** @file Normalizes one vertex's influences: merge duplicates, cap at 4, prune < 0.005, renormalize. */
import type { Id } from '@pose2d/format/types';
import { sameWeights } from './sameWeights';

type Influence = [Id, number];

/** Influences kept per vertex (research §4.3). */
const MAX_INFLUENCES = 4;
/** Shares of the capped total below this are dropped (the strongest influence always stays). */
const PRUNE = 0.005;
/** A kept sum this close to 1 is already normalized and is not divided again (division would drift by ULPs, W2-31). */
const SUM_TOLERANCE = 8 * Number.EPSILON;

/** Sorts in place by weight descending, then bone id ascending (bones are distinct after merging: a total order). */
const byWeight = (list: Influence[]): Influence[] => list.sort((a, b) => b[1] - a[1] || (a[0] < b[0] ? -1 : 1));
/** Sum of the weights, in list order. */
const sumOf = (list: readonly Influence[]): number => list.reduce((s, [, w]) => s + w, 0);

/**
 * Factor 2^-k (smallest k with 2^k ≥ the largest weight) when a weight exceeds 1, else 1 (R6-01): merging huge finite
 * weights stays finite, and a power-of-two scale is exact, so results equal the unscaled ones wherever those were finite.
 */
function preScale(influences: readonly (readonly [Id, number])[]): number {
  let largest = 0;
  for (const [, w] of influences) if (w > largest) largest = w;
  return largest > 1 ? 2 ** -Math.ceil(Math.log2(largest)) : 1;
}

/** One pass: pre-scale, merge weights > 0, sort, cap, prune, then divide by the kept sum unless it is 1 within SUM_TOLERANCE. */
function normalizePass(influences: readonly (readonly [Id, number])[]): Influence[] | null {
  const scale = preScale(influences);
  const merged = new Map<Id, number>();
  for (const [bone, w] of influences) {
    const scaled = w * scale;
    if (scaled > 0) merged.set(bone, (merged.get(bone) ?? 0) + scaled);
  }
  const capped = byWeight([...merged]).slice(0, MAX_INFLUENCES);
  const total = sumOf(capped);
  if (!(total > 0)) return null;
  const kept = capped.filter(([, w], i) => i === 0 || w / total >= PRUNE);
  const sum = sumOf(kept);
  return Math.abs(sum - 1) <= SUM_TOLERANCE ? kept : byWeight(kept.map(([bone, w]): Influence => [bone, w / sum]));
}

/** Sorted [bone, weight] pairs (weight desc, then bone id asc) summing to 1; null when no weight is > 0; idempotent. */
export function normalizeVertexWeights(influences: readonly (readonly [Id, number])[]): [Id, number][] | null {
  // Idempotent (W2-31): an already normalized list comes back unchanged, so sending a mesh its current weights is a
  // no-op. A pass maps its own output to itself unless rounding puts a share just under PRUNE; each such pass drops
  // one influence, so the fixed point is reached within MAX_INFLUENCES more passes.
  let out = normalizePass(influences);
  for (let i = 0; out !== null && i < MAX_INFLUENCES; i++) {
    const again = normalizePass(out);
    if (again === null || sameWeights([again], [out])) break;
    out = again;
  }
  return out;
}
